// LandingPage — Produktseite unter didadigitalassistant.de.
// Apple-Stil: Sektionen morphen beim Scrollen herein (IntersectionObserver +
// CSS-Transitions), die "Screenshots" sind lebende Mini-Mockups aus den
// echten App-Bausteinen — Termine gleiten ein, Kennzahlen zählen hoch.
import { useState, useEffect, useRef } from "react";
import { DidaLogo, DidaWordmark, Picto, Btn } from "./ui";

// ── Scroll-Helfer ────────────────────────────────────────────────────────────
function useReveal(threshold = 0.22) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { el?.classList.add("in"); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("in"); io.disconnect(); }
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return ref;
}

function CountUp({ to, suffix = "", dur = 1300 }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setV(to); return; }
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const step = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, dur]);
  return <span ref={ref}>{v.toLocaleString("de-DE")}{suffix}</span>;
}

// ── Geräterahmen für die Mockups ─────────────────────────────────────────────
const Frame = ({ children, style = {} }) => (
  <div style={{ background: "var(--mf-bg)", border: "0.5px solid var(--mf-border)", borderRadius: 18,
    boxShadow: "0 24px 60px -18px rgba(15,110,86,.25), 0 0 0 0.5px rgba(60,60,67,.06)", overflow: "hidden", ...style }}>
    <div style={{ display: "flex", gap: 5, padding: "9px 12px", borderBottom: "0.5px solid var(--mf-border)", background: "var(--mf-bg-alt)" }}>
      {["#FF5F57", "#FEBC2E", "#28C840"].map(c => <span key={c} style={{ width: 9, height: 9, borderRadius: "50%", background: c }} />)}
      <span style={{ fontSize: 9, color: "var(--mf-muted)", marginLeft: 6 }}>dida</span>
    </div>
    <div style={{ padding: 14 }}>{children}</div>
  </div>
);

// ── Lebende Mockups (echte App-Optik, animiert) ─────────────────────────────
function MockDashboard() {
  const bars = [34, 52, 44, 66, 58, 82];
  return (
    <Frame>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Guten Morgen! <span style={{ fontWeight: 400, color: "var(--mf-muted)", fontSize: 10 }}>· 14 Termine heute</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(90px,1fr))", gap: 7, marginBottom: 10 }}>
        {[["€ ", 18450, "Umsatz Monat"], ["", 87, "HKP-Quote %"], ["", 74, "Auslastung %"], ["", 3, "No-Shows"]].map(([pre, val, l]) => (
          <div key={l} style={{ background: "var(--mf-bg-alt)", borderRadius: 12, padding: "9px 10px" }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{pre}<CountUp to={val} /></div>
            <div style={{ fontSize: 8.5, color: "var(--mf-muted)", marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 46 }}>
        {bars.map((h, i) => (
          <div key={i} className="lp-bar" style={{ flex: 1, height: `${h}%`, borderRadius: "3px 3px 0 0",
            background: i === bars.length - 1 ? "var(--mf-primary)" : "rgba(53,201,155,.35)", animationDelay: `${0.15 + i * 0.09}s` }} />
        ))}
      </div>
    </Frame>
  );
}

function MockTermine() {
  const apts = [
    ["08:00", "Weber, Klaus", "Kontrolle", "rgba(53,201,155,.16)"],
    ["09:00", "Maier, Maria", "PZR", "rgba(96,165,250,.16)"],
    ["10:30", "Fischer, Anna", "Beratung", "rgba(167,139,250,.16)"],
  ];
  return (
    <Frame>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Terminplan · heute</div>
      {apts.map(([t, p, typ, bg], i) => (
        <div key={t} className="lp-apt" style={{ display: "flex", gap: 8, alignItems: "center", background: bg, borderRadius: 10, padding: "7px 10px", marginBottom: 6, animationDelay: `${0.15 + i * 0.14}s` }}>
          <span style={{ fontSize: 10, fontWeight: 700, width: 34 }}>{t}</span>
          <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{p}</span>
          <span style={{ fontSize: 9, color: "var(--mf-muted)" }}>{typ}</span>
        </div>
      ))}
      <div className="lp-apt lp-absage" style={{ display: "flex", gap: 8, alignItems: "center", borderRadius: 10, padding: "7px 10px", marginBottom: 6, animationDelay: ".6s" }}>
        <span style={{ fontSize: 10, fontWeight: 700, width: 34 }}>11:30</span>
        <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>Braun, Max</span>
        <span style={{ fontSize: 9, color: "var(--mf-neg)", fontWeight: 600 }}>abgesagt</span>
      </div>
      <div className="lp-chip" style={{ display: "flex", alignItems: "center", gap: 7, background: "var(--mf-bg-alt)", border: "0.5px solid var(--mf-border)", borderRadius: 10, padding: "7px 10px" }}>
        <Picto.Calendar size={13} />
        <span style={{ fontSize: 10 }}>Lücke füllen? <strong>Wolf, Inge</strong> — PZR überfällig seit 94 Tagen</span>
        <span style={{ marginLeft: "auto", fontSize: 9.5, fontWeight: 700, color: "var(--mf-primary)" }}>Einbuchen</span>
      </div>
    </Frame>
  );
}

function MockRecall() {
  const rows = [["Wolf, Inge", "vor 94 Tagen"], ["Koch, Birgit", "vor 71 Tagen"], ["Müller, Ralf", "vor 63 Tagen"]];
  return (
    <Frame>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Recall · überfällig</div>
      {rows.map(([n, t], i) => (
        <div key={n} className="lp-apt" style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 4px", borderBottom: "0.5px solid var(--mf-border)", animationDelay: `${0.15 + i * 0.12}s` }}>
          <span className="lp-check" style={{ width: 15, height: 15, borderRadius: 4, background: "var(--mf-primary)", color: "#fff", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", animationDelay: `${0.8 + i * 0.25}s` }}>✓</span>
          <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{n}</span>
          <span style={{ fontSize: 9.5, color: "var(--mf-neg)" }}>{t}</span>
        </div>
      ))}
      <div className="lp-toast" style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6, background: "var(--mf-primary)", color: "#fff", borderRadius: 10, padding: "7px 12px", fontSize: 10.5, fontWeight: 600 }}>
        3 Recall-Mails gesendet ✓
      </div>
    </Frame>
  );
}

function MockFeedback() {
  return (
    <Frame>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Wie war Ihr Besuch?</div>
      <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
        {[0, 1, 2, 3, 4].map(i => (
          <span key={i} className="lp-star" style={{ fontSize: 22, color: "#F0A02F", animationDelay: `${0.2 + i * 0.18}s` }}>★</span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
        {Array.from({ length: 11 }, (_, i) => (
          <span key={i} style={{ width: 20, height: 20, borderRadius: 6, fontSize: 9.5, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center",
            background: i === 9 ? "var(--mf-primary)" : "var(--mf-bg-alt)", color: i === 9 ? "#fff" : "var(--mf-muted)" }}>{i}</span>
        ))}
      </div>
      <div style={{ borderTop: "0.5px solid var(--mf-border)", paddingTop: 9, fontSize: 10, color: "var(--mf-muted)" }}>
        Feedback je Behandler · <strong style={{ color: "var(--mf-text)" }}>Ø <CountUp to={4} />,<CountUp to={6} dur={900} /> ★</strong> · NPS <strong style={{ color: "var(--mf-primary)" }}><CountUp to={62} /></strong>
      </div>
    </Frame>
  );
}

// ── Bausteine ────────────────────────────────────────────────────────────────
const H2 = ({ children }) => <h2 style={{ fontSize: "clamp(22px,3.4vw,30px)", fontWeight: 700, textAlign: "center", margin: "0 0 8px", lineHeight: 1.2 }}>{children}</h2>;
const Sub = ({ children }) => <p style={{ fontSize: 13.5, color: "var(--mf-muted)", textAlign: "center", margin: "0 auto 30px", maxWidth: 560, lineHeight: 1.6 }}>{children}</p>;

function FeatureRow({ flip, kicker, titel, text, mock }) {
  const ref = useReveal();
  const mockRef = useReveal(0.3);
  return (
    <div style={{ display: "flex", gap: "28px 44px", alignItems: "center", flexWrap: "wrap", padding: "34px 0", flexDirection: flip ? "row-reverse" : "row" }}>
      <div ref={ref} className={`lp-reveal ${flip ? "lp-rechts" : "lp-links"}`} style={{ flex: "1 1 300px", minWidth: 270 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: "var(--mf-primary)", textTransform: "uppercase", marginBottom: 8 }}>{kicker}</div>
        <div style={{ fontSize: "clamp(19px,2.6vw,24px)", fontWeight: 700, marginBottom: 10, lineHeight: 1.25 }}>{titel}</div>
        <div style={{ fontSize: 13.5, color: "var(--mf-muted)", lineHeight: 1.65 }}>{text}</div>
      </div>
      <div ref={mockRef} className={`lp-reveal lp-zoom lp-mock`} style={{ flex: "1 1 320px", minWidth: 280, maxWidth: 460 }}>
        {mock}
      </div>
    </div>
  );
}

// ── Seite ────────────────────────────────────────────────────────────────────
export default function LandingPage({ onLogin }) {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  // Hero-Mockup: neigt sich beim Scrollen sanft auf (Apple-Parallax)
  const heroMockRef = useRef(null);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = heroMockRef.current;
        if (!el) return;
        const p = Math.min(1, Math.max(0, window.scrollY / 420));
        el.style.transform = `perspective(1100px) rotateX(${(1 - p) * 16}deg) translateY(${(1 - p) * 14}px) scale(${0.94 + p * 0.06})`;
        el.style.opacity = String(0.75 + p * 0.25);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const claimRef = useReveal();
  const stufenRef = useReveal();
  const trustRef = useReveal();
  const ctaRef = useReveal();

  return (
    <div style={{ minHeight: "100vh", background: "var(--mf-bg-alt)", color: "var(--mf-text)", overflowX: "hidden" }}>

      {/* Nav */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, display: "flex", alignItems: "center", gap: 10, padding: "12px 20px",
        background: "color-mix(in srgb, var(--mf-bg) 82%, transparent)", backdropFilter: "saturate(1.4) blur(14px)", WebkitBackdropFilter: "saturate(1.4) blur(14px)",
        borderBottom: "0.5px solid var(--mf-border)" }}>
        <DidaLogo size={30} /><DidaWordmark size={17} />
        <div style={{ flex: 1 }} />
        <span onClick={() => scrollTo("funktionen")} style={{ fontSize: 12, color: "var(--mf-muted)", cursor: "pointer", padding: "6px 8px" }}>Funktionen</span>
        <span onClick={() => scrollTo("betrieb")} style={{ fontSize: 12, color: "var(--mf-muted)", cursor: "pointer", padding: "6px 8px" }}>Einsatz</span>
        <Btn color="g" onClick={onLogin}>Anmelden</Btn>
      </div>

      {/* Hero */}
      <div style={{ position: "relative", maxWidth: 920, margin: "0 auto", padding: "64px 20px 30px", textAlign: "center" }}>
        <div style={{ position: "absolute", top: -120, right: -140, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle,rgba(53,201,155,0.35),transparent 65%)", filter: "blur(50px)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -60, left: -160, width: 460, height: 460, borderRadius: "50%", background: "radial-gradient(circle,rgba(15,110,86,0.25),transparent 65%)", filter: "blur(60px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", animation: "dida-fade-up .5s ease-out both" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><DidaLogo size={66} /></div>
          <h1 style={{ fontSize: "clamp(30px,5.4vw,48px)", fontWeight: 700, margin: "0 0 14px", lineHeight: 1.12 }}>
            Weniger verwalten.<br />Mehr behandeln.
          </h1>
          <p style={{ fontSize: "clamp(14px,2.2vw,17px)", color: "var(--mf-muted)", lineHeight: 1.6, maxWidth: 600, margin: "0 auto 24px" }}>
            dida ist die digitale Praxisassistenz für Zahnarztpraxen: geteilter
            Terminplan auf allen Geräten, Recall-Erinnerungen, die wirklich rausgehen,
            und Kennzahlen aus echten Daten statt Bauchgefühl.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <Btn color="g" lg onClick={onLogin}>Anmelden</Btn>
            <Btn lg onClick={() => scrollTo("funktionen")}>Funktionen ansehen</Btn>
          </div>
        </div>
        <div ref={heroMockRef} className="lp-mock in" style={{ position: "relative", maxWidth: 560, margin: "44px auto 0", willChange: "transform", transition: "opacity .3s" }}>
          <MockDashboard />
        </div>
      </div>

      {/* Claim-Band */}
      <div ref={claimRef} className="lp-reveal" style={{ maxWidth: 760, margin: "0 auto", padding: "56px 20px 20px", textAlign: "center" }}>
        <div style={{ fontSize: "clamp(20px,3.2vw,28px)", fontWeight: 700, lineHeight: 1.35 }}>
          Dein PVS rechnet ab.{" "}
          <span style={{ color: "var(--mf-primary)" }}>dida zeigt, wie viel.</span>
        </div>
        <p style={{ fontSize: 13.5, color: "var(--mf-muted)", lineHeight: 1.65, maxWidth: 560, margin: "12px auto 0" }}>
          Keine Migration, keine Doppelpflege: dida arbeitet mit den Daten, die du
          ohnehin hast — CSV-Exporte, Kalender, Termine — und macht daraus ein
          Werkzeug für den ganzen Praxisalltag.
        </p>
      </div>

      {/* Features mit lebenden Mockups */}
      <div id="funktionen" style={{ maxWidth: 980, margin: "0 auto", padding: "24px 20px 30px" }}>
        <FeatureRow kicker="Terminplan" titel="Ein Plan. Alle Geräte. Keine Lücken."
          text="Empfang, Behandlungszimmer und unterwegs sehen denselben Plan — live aus der Datenbank. Und wenn ein Patient absagt, schlägt dida sofort vor, wer die Lücke füllen kann: überfällige Recalls zuerst."
          mock={<MockTermine />} />
        <FeatureRow flip kicker="Recall & Mail" titel="Erinnerungen, die wirklich rausgehen."
          text="dida erkennt fällige Prophylaxe-Patienten automatisch aus Terminen und Patientenstamm. Auswählen, Text prüfen, senden — die Mails laufen über EU-Server, mit deinem Praxisnamen als Absender."
          mock={<MockRecall />} />
        <FeatureRow kicker="Tracking" titel="Berechnen statt behaupten."
          text="HKP-Quote, Auslastung, No-Shows, Churn: jede Kennzahl entsteht aus deinen echten Daten — und wo eine Quelle fehlt, sagt dida das ehrlich. Der Potenzial-Rechner zeigt, was kleine Verbesserungen im Jahr wert wären."
          mock={<MockDashboard />} />
        <FeatureRow flip kicker="Feedback" titel="Wissen, wie es den Patienten wirklich geht."
          text="Anonyme 1-Minuten-Umfrage nach dem Besuch, ausgewertet bis auf Behandlerebene — daneben deine Google-Bewertungen live und ein anonymer Team-Puls, damit du auch die Stimmung im Team im Blick hast."
          mock={<MockFeedback />} />
      </div>

      {/* Einsatzstufen */}
      <div id="betrieb" style={{ maxWidth: 980, margin: "0 auto", padding: "26px 20px 46px" }}>
        <H2>dida passt sich deiner Praxis an.</H2>
        <Sub>Drei Einsatzstufen — du entscheidest, wie viel dida übernimmt, und kannst jederzeit wachsen.</Sub>
        <div ref={stufenRef} className="lp-stagger" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {[["STUFE 1", "Tracking-Ebene", "Dein PVS bleibt führend. dida liest CSV-Exporte und Kalender und liefert Kennzahlen, Recall und Potenziale obendrauf."],
            ["STUFE 2", "PVS-Ergänzung", "Terminplan, Recall, Mail und Feedback laufen in dida — die Abrechnung bleibt, wo sie ist."],
            ["STUFE 3", "Praxiszentrale", "dida als tägliches Arbeitswerkzeug für das ganze Team: Termine, Patienten, Rezepte, Team und Auswertung an einem Ort."]].map(([nr, titel, text]) => (
            <div key={nr} style={{ flex: "1 1 240px", minWidth: 220, background: "var(--mf-bg)", border: "0.5px solid var(--mf-border)", borderRadius: 16, padding: "18px 16px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mf-primary)", letterSpacing: 1 }}>{nr}</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, margin: "6px 0 4px" }}>{titel}</div>
              <div style={{ fontSize: 11.5, color: "var(--mf-muted)", lineHeight: 1.55 }}>{text}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Trust */}
      <div ref={trustRef} className="lp-reveal" style={{ display: "flex", gap: "8px 22px", justifyContent: "center", flexWrap: "wrap", padding: "0 20px 46px", fontSize: 11.5, color: "var(--mf-muted)" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Picto.Lock size={15} stroke="var(--mf-muted)" />Verschlüsselte Verbindung</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Picto.Shield size={15} stroke="var(--mf-muted)" />Server in Frankfurt · DSGVO</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Picto.Mail size={15} stroke="var(--mf-muted)" />Mail-Versand über EU-Server</span>
      </div>

      {/* CTA */}
      <div ref={ctaRef} className="lp-reveal lp-zoom" style={{ maxWidth: 640, margin: "0 auto", padding: "0 20px 64px", textAlign: "center" }}>
        <div style={{ background: "var(--mf-bg)", border: "0.5px solid var(--mf-border)", borderRadius: 20, padding: "30px 24px" }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>Pilotpraxis werden</h2>
          <p style={{ fontSize: 13, color: "var(--mf-muted)", lineHeight: 1.6, margin: "0 0 18px" }}>
            dida entsteht gerade im echten Praxisbetrieb. Als Pilotpraxis nutzt du alles
            kostenlos und bestimmst mit, was als Nächstes gebaut wird.
          </p>
          <Btn color="g" lg onClick={() => { window.location.href = "mailto:mail@didadigitalassistant.de?subject=Pilotpraxis%20werden"; }}>Kontakt aufnehmen</Btn>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: "0.5px solid var(--mf-border)", padding: "18px 20px calc(18px + env(safe-area-inset-bottom))", display: "flex", gap: "6px 18px", alignItems: "center", flexWrap: "wrap", justifyContent: "center", fontSize: 11, color: "var(--mf-muted)" }}>
        <span>© {new Date().getFullYear()} dida — digital dental assistant</span>
        {/* TODO (rechtlich, macht Emre): echte Seiten für Impressum & Datenschutz verlinken */}
        <span style={{ cursor: "pointer" }} onClick={() => alert("Impressum folgt — dida befindet sich in der Pilotphase.")}>Impressum</span>
        <span style={{ cursor: "pointer" }} onClick={() => alert("Datenschutzerklärung folgt — dida befindet sich in der Pilotphase.")}>Datenschutz</span>
      </div>
    </div>
  );
}
